import { useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

// S2 endpoint — group search
const API_END_POINT = "group/search/";

// Group types are an array of strings here
export type GroupSearchType = string[];

export interface GroupSearchFilter {
	search?: string;
	active_only?: number;
	type?: GroupSearchType;
	limit: number;
	page?: number;
	mbr_size_limit?: number;
	id?: string;
	g?: string;
}

// Construct query string for api query
const getQueryString = (filter: GroupSearchFilter) => {
	let qs = "";
	if (filter?.limit) {
		qs += "limit=" + filter.limit + "&";
	}
	if (filter?.page) {
		qs += "page=" + filter.page + "&";
	}
	if (filter?.id) {
		qs += "group_id=" + filter.id + "&";
	}
	if (filter?.search) {
		qs += "search=" + filter.search + "&";
	}
	if (filter?.active_only !== undefined) {
		qs += "active_only=" + filter.active_only + "&";
	}
	if (Array.isArray(filter?.type) && filter.type.length > 0) {
		qs += "type=" + filter.type.join(",") + "&";
	}
	if (filter?.mbr_size_limit) {
		qs += "mbr_size_limit=" + filter.mbr_size_limit + "&";
	}
	if (filter?.g !== undefined) {
		qs += "g=" + filter.g;
	}

	qs = qs ? "?" + qs : qs;
	return qs;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sortResult = (data: any[]) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return [...data].sort(function (a: any, b: any) {
		try {
			if (a.Name.Short && b.Name.Short) {
				const nameA = a.Name.Short.toUpperCase();
				const nameB = b.Name.Short.toUpperCase();
				if (nameA < nameB) return -1;
				if (nameA > nameB) return 1;
			}
		} catch {
			/* squash */
		}
		return 0;
	});
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const preProcessResult = (data: any) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return data.map((d: any) => {
		d.id = d._id;
		return d;
	});
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processGroupSearchResult = (res: any[]): any[] => {
	if (res) {
		let newRes = preProcessResult(res);
		newRes = sortResult(newRes);
		return newRes;
	}
	return [];
};

/**
 * Hook to fetch api data from group search S2 API.
 */
export function useGroupSearch() {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const [groupSearchFilter, setGroupSearchFilter] =
		useState<GroupSearchFilter>({
			search: "",
			active_only: 1,
			type: [],
			limit: 10,
			page: 1,
			mbr_size_limit: 0,
			id: "",
			g: "",
		});

	const { data: groupSearchResult = [], isPending } = useQuery({
		queryKey: ["groupSearch", groupSearchFilter],
		queryFn: async () => {
			const queryString = getQueryString(groupSearchFilter);
			const res = await callPymbleApi(
				instance,
				"GET",
				API_END_POINT + queryString
			);
			return processGroupSearchResult(res);
		},
		enabled: isAuthenticated,
	});

	const refreshGroupSearchResult = async () => {
		// No-op: React Query auto-refetches when queryKey changes.
		// Kept for backward compatibility.
	};

	return {
		groupSearchResult,
		groupSearchResultLoaded: !isPending,
		refreshGroupSearchResult,
		groupSearchFilter,
		setGroupSearchFilter,
		processGroupSearchResult,
	};
}
